/* eslint-disable no-param-reassign */
import { Utilities, CustomError } from '../common';
/**
 * Creates the default data for the cls model
 *
 * @return {Object} - Default clr data
 */
export const createDefaultClsData = () => ({
  // client information
  clientInformation: {
    errors: [], // errors
    // flags
    flags: {
      flagViewTime: 0, // flag-viewtime ({A, B})
      flagPiFrequency: 0, // flag-frequency (counter)
      flagPiReload: 0, // flag-reloads (counter)
      transmissionError: 0, // transmission-error (counter)
      flagPiBurst: 0, // flag-pi-burst (counter)
      sessionStart: 0, // Session start (unix time)
    },
  },
  // site
  site: {
    firstEvent: 0, // first-event
    lastTransaction: 0, // last-event
    lastSite: '', // last-site
    lastCode: '', // last-code
    lastEvent: '', // last-event
    lastChecksum: '', // last-checksum
    eventTime: 0, // last-event-time
    lastSequenceNumber: 0, // last sequence number
  },
  // code
  code: {
    // specific-code
    // "code-name": {
    //  firstEvent: "", //first-event
    //  "la": ""  //last-event
    // }
  },
  // url-cache
  urlCache: {
    // specific-category
    // "url": {
    //  firstEvent: "", //first-event
    // }
  },
  // profile
  profile: Utilities.createDefaultProfile(),
});
/**
 * Initializes a cls data model instance. Worst run time is O(n^3)
 *
 * @param {ClsData} clsData - Current client data with sensor instance and
 *   current data model
 */
const init = (clsData) => {
  const {
    now,
  } = clsData;
  const {
    basicSettings,
    clrData,
    logger,
    state,
    systemConfig,
  } = clsData.sensor;
  // Create a deep copy to avoid side effects when counting measured data up.
  const relayData = clrData.data;
  // Create a today object
  const today = Utilities.today();
  // Check if we have an pi event and start initialization when true
  if (state.globalParams.eventHandling === false || (state.globalParams.eventHandling === true && basicSettings.piEventList.indexOf(state.globalEvent) > -1)) {
    logger.debug('creating local data');
    // Map some properties from the current state over to the current data
    clsData.data.site.firstEvent = now;
    clsData.data.site.lastTransaction = now;
    clsData.data.site.lastSite = state.globalParams.site;
    clsData.data.site.lastCode = state.globalParams.code;
    clsData.data.site.eventTime = now;
    clsData.data.site.lastEvent = state.globalEvent;
    clsData.data.site.lastSequenceNumber = relayData.measuredData.site.sequenceNumber;
    // Map profile site stats from clr data model over to the current data when profiling is activated
    if (systemConfig.profiling.enabled) {
      clsData.data.profile = Utilities.deepClone(clsData.data.profile);
      clsData.data.profile.site.loyalty[today] = {};
      // Iterate over each metric in basic settings -> basic metrics
      for (let i = 0, iLen = basicSettings.basicMetrics.length; i < iLen; i += 1) {
        const metric = basicSettings.basicMetrics[i];
        // Map each metric from clr site data over to current site data
        clsData.data.profile.site[metric] = relayData.measuredData.site[metric];
        // Map each metric from clr site loyalty over to current site loyalty data
        clsData.data.profile.site.loyalty[today][metric] = relayData.measuredData.site[metric];
      }
      // Map view time from the clr last transaction data to current site view time
      clsData.data.profile.site.loyalty[today].viewtime = relayData.measuredData.site.viewtime;
    }
    // Create code
    clsData.data.code[state.globalParams.code] = { firstEvent: now, lastTransaction: now };
  }
};

/**
 * Rewrites the cls data
 *
 * @param {ClsData} clsData - Current cls data instance
 */
const rewrite = (clsData) => {
  const {
    now,
  } = clsData;
  const {
    basicSettings,
    clrData,
    state,
    systemConfig,
  } = clsData.sensor;
  // Create a deep copy to avoid side effects when counting measured data up.
  const relayData = clrData.data;
  // Create a today object
  const today = Utilities.today();
  if (state.globalParams.eventHandling === false || (state.globalParams.eventHandling === true && basicSettings.piEventList.indexOf(state.globalEvent) > -1)) {
    // Overwrite last transaction in code if necessary or create a new code data structure
    if (Object.keys(clsData.data.code).indexOf(state.globalParams.code) > -1) {
      clsData.data.code[state.globalParams.code].lastTransaction = now;
    } else {
      clsData.data.code[state.globalParams.code] = { firstEvent: now, lastTransaction: now };
    }
    // Detect fraud traffic and flag measured data with detection results
    if (systemConfig.fraudDetection.enabled) {
      // Fraud prevention: Mark extreme usage
      if (clsData.data.clientInformation.flags.flagViewTime !== 'B') {
        const viewtimeFlagKeys = Object.keys(systemConfig.fraudDetection.viewtime);
        for (let i = 0, iLen = viewtimeFlagKeys.length; i < iLen; i += 1) {
          const key = viewtimeFlagKeys[i];
          const limit = systemConfig.fraudDetection.viewtime[key];
          if (now - clsData.data.clientInformation.flags.sessionStart > limit) {
            clsData.data.clientInformation.flags.flagViewTime = systemConfig.fraudDetection.viewtime[key];
          }
        }
      }
      // Fraud detection: Pi burst detection
      if (now - clsData.data.site.lastTransaction > 0 && now - clsData.data.site.lastTransaction < systemConfig.fraudDetection.piBurst) {
        clsData.data.clientInformation.flags.flagPiBurst += relayData.measuredData.site.pi;
      }

      // Fraud prevention: Pi frequency check
      if (now - clsData.data.site.lastTransaction === clsData.data.clientInformation.flags.lastViewtime) {
        clsData.data.clientInformation.flags.flagPiFrequency += relayData.measuredData.site.pi;
      }

      // Fraud prevention: reload or single content usage
      if (clsData.data.site.lastCode === state.globalParams.code) {
        clsData.data.clientInformation.flags.flagPiReload += relayData.measuredData.site.pi;
      }
    }

    // Map profiling site stats when profiling is enabled
    if (systemConfig.profiling.enabled === true) {
      clsData.data.profile = Utilities.deepClone(clsData.data.profile);
      // Check if today's loyalty is present in current site profile and save check result is local variable
      const hasLoyalty = Object.keys(clsData.data.profile.site.loyalty).indexOf(today) > -1;
      // Update today's loyalty viewtime if necessary
      if (hasLoyalty === true) {
        clsData.data.profile.site.loyalty[today].viewtime += relayData.measuredData.site.viewtime;
      } else {
        // Create new loyalty for to day and set view time
        clsData.data.profile.site.loyalty[today] = {};
        clsData.data.profile.site.loyalty[today].viewtime = relayData.measuredData.site.viewtime;
      }
      // Start update for all metrics
      for (let i = 0, iLen = basicSettings.basicMetrics.length; i < iLen; i += 1) {
        // Extract metric key
        const metric = basicSettings.basicMetrics[i];
        // Extract metric value
        const metricValue = relayData.measuredData.site[metric];
        // Map metric value to profile site
        clsData.data.profile.site[metric] += metricValue;
        // Update today's loyalty if necessary
        if (hasLoyalty === true) {
          // Add new metric value to today's loyalty
          clsData.data.profile.site.loyalty[today][metric] += metricValue;
        } else {
          // Set today's loyalty to metric value
          clsData.data.profile.site.loyalty[today][metric] = metricValue;
        }
      }
      clsData.data.profile.site.hourlyWnk += relayData.measuredData.site.hourlyWnk;
      clsData.data.profile.site.viewtime += relayData.measuredData.site.viewtime;
    }

    // Rewrite site related data
    clsData.data.site.lastTransaction = now;
    clsData.data.site.lastSite = state.globalParams.site;
    clsData.data.site.lastCode = state.globalParams.code;
    clsData.data.site.lastSequenceNumber = relayData.measuredData.site.sequenceNumber;
  }
  clsData.data.site.eventTime = now;
};

/**
 * Cleans up the cached code, categories, loyalty and cat visit data
 *
 * @param {ClsData} clsData - Instance of the cls data model class
 */
const cleanup = (clsData) => {
  const {
    now,
  } = clsData;
  const {
    logger,
    systemConfig,
    basicSettings,
  } = clsData.sensor;
  // Create code array which is sorted by last transaction ascending
  const codes = Object
    .keys(clsData.data.code)
    .map(key => ({ key, lastTransaction: clsData.data.code[key].lastTransaction }))
    .sort((a, b) => a.lastTransaction < b.lastTransaction)
    .map(code => code.key);
  const profileCategories = Object.keys(clsData.data.profile.category);
  const urls = Object.keys(clsData.data.urlCache);
  const loyaltyDays = Object.keys(clsData.data.profile.site.loyalty);
  const maxLoyalty = new Date(new Date().setDate(new Date().getDate() - systemConfig.profiling.loyalty.ttl));
  const maxLoyaltyText = maxLoyalty
    .toJSON()
    .split('T')
    .shift()
    .replace(/-/g, '');
  const timeout = systemConfig.cacheTimeout;
  // Remove cached codes who reached the cache timeout
  for (let i = 0, iLen = codes.length; i < iLen; i += 1) {
    const code = codes[i];
    if (now - clsData.data.code[code].lastTransaction > timeout) {
      delete clsData.data.code[code];
      logger.debug(`Code cleanup by timout: ${code}`);
    }
  }
  // Limit maximum stored codes to 10 codes
  if (codes.length > basicSettings.maxStoredCodes) {
    // Get the first code from the dictionary
    const firstCode = codes[0];
    // Delete the first code in the dictionary
    delete clsData.data.code[firstCode];
    logger.debug(`Code cleanup by length: ${firstCode}`);
  }
  // Remove cached urls who reached the cache timout
  for (let i = 0, iLen = urls.length; i < iLen; i += 1) {
    const url = urls[i];
    if (now - clsData.data.urlCache[url].lastTransaction > timeout) {
      logger.debug(`url cleanup: ${url}`);
      delete clsData.data.urlCache[url];
    }
  }
  // Cleanup loyalty history for entries who reached the maximum loyalty cache time
  logger.debug(`first loyalty day: ${maxLoyaltyText}`);
  for (let i = 0, iLen = loyaltyDays.length; i < iLen; i += 1) {
    const loyaltyDay = loyaltyDays[i];
    if (maxLoyalty > Date.parse(loyaltyDay)) {
      logger.debug(`loyalty cleanup: ${loyaltyDay}`);
      delete clsData.data.profile.site.loyalty[loyaltyDay];
    }
  }

  // Cleanup loyalty history in profiled categories
  for (let i = 0, iLen = profileCategories.length; i < iLen; i += 1) {
    const profileCategory = profileCategories[i];
    const profileCategoryLoyaltyDays = Object.keys(clsData.data.profile.category[profileCategory].loyalty);
    for (let j = 0, jLen = profileCategoryLoyaltyDays.length; j < jLen; j += 1) {
      const profileCategoryLoyaltyDay = profileCategoryLoyaltyDays[i];
      if (maxLoyalty > Date.parse(profileCategoryLoyaltyDay)) {
        logger.debug(`loyalty cleanup for profiled category ${profileCategory}: ${profileCategoryLoyaltyDay}`);
        delete clsData.data.profile.category[profileCategory].loyalty[profileCategoryLoyaltyDay];
      }
    }
  }
};

/**
 * Sends the last transaction and event time to the native app when the sensor is running in an acsam capable webview
 *
 * @param {ClsData} clsData - Cls data instance
 */
const informNativeApp = (clsData) => {
  const { sensor } = clsData;
  const { state, basicSettings } = sensor;
  if (state.hybrid.detected === true) {
    if (state.hybrid.platform === 'ios') {
      window.webkit.messageHandlers[basicSettings.webviewDetection.ios].postMessage(JSON.stringify({
        type: 'inform',
        payload: {
          lastTransaction: clsData.data.site.lastTransaction,
          lastEventTime: clsData.data.site.eventTime,
        },
      }));
    }
    if (state.hybrid.platform === 'android') {
      window[basicSettings.webviewDetection.android]
        .setWebViewData(clsData.data.site.lastTransaction.toString(), clsData.data.site.eventTime.toString());
    }
  }
};

/**
 * Cls data model class
 */
export default class ClsData {
  /**
   * Initiates a new instance of cls data model class
   *
   * @param {Sensor} sensor - Current sensor instance
   */
  constructor(sensor) {
    this.name = 'ClsDataClass';
    this.sensor = sensor;
    this.now = this.sensor.state.now.getTime();
    this.data = createDefaultClsData();
  }

  /**
   * Updates the current data
   * @public
   */
  update() {
    return new Promise((resolve) => {
      const {
        crypto,
        state,
      } = this.sensor;
      if (this.data.site.firstEvent === 0) {
        init(this);
      } else {
        rewrite(this);
      }
      this.data.site.lastEvent = state.globalEvent;
      // Inform native app when sensor is running in a acsam capable webview
      informNativeApp(this);
      try {
        crypto
          .hash(JSON.stringify(this.data))
          .then((checksum) => {
            this.data.site.lastChecksum = checksum;
            cleanup(this);
            resolve();
          })
          .catch(error => state.scriptErrors.push(new CustomError('E7', error.message)));
      } catch (error) {
        state.scriptErrors.push(new CustomError('E7', error.message));
        cleanup(this);
        resolve();
      }
    });
  }
}
